import React, { useEffect } from "react";
import {
  useTable,
  useGlobalFilter,
  Column,
  usePagination,
} from "react-table";
import { TiMediaPlay, TiMediaPlayReverse } from "react-icons/ti";

interface TableProps<T extends object> {
  columns: Column<T>[];
  data: T[];
  filterValue: string;
  showPagination?: boolean; // Add a prop to control pagination visibility
}

export function Table<T extends { id: string }>({
  filterValue,
  columns,
  data,
  showPagination = true, // Default to true for pagination visibility
}: TableProps<T>): React.ReactElement {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setGlobalFilter,

    page, // Instead of using 'rows', we'll use page,
    // which has only the rows for the active page

    // The rest of these things are super handy, too ;)
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable<T>(
    {
      columns,
      data,
      initialState: { pageIndex: 0,   pageSize: 2000 },
    },
    useGlobalFilter,
    usePagination
  );

  useEffect(() => {
    handleFilterInputChange(filterValue);
  }, [filterValue]);

  const handleFilterInputChange = (value: string) => {
    setGlobalFilter(value);
  };

  return (
    <div className="base-table-component">
      {/* Your table content */}
      <table {...getTableProps()} className="table">
        <thead>
          {headerGroups.map((headerGroup, index) => (
            <tr {...headerGroup.getHeaderGroupProps()} key={index}>
              {headerGroup.headers.map((column, index) => (
                <th {...column.getHeaderProps()} key={index}>
                  {column.render("Header")}
                  {/* Render the columns filter UI */}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows?.map((row, i) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()} key={i}>
                {row.cells.map((cell,cellIndex) => {
                  return <td {...cell.getCellProps()} key={cellIndex}>{cell.render("Cell")}</td>;
                })}
              </tr>
            );
          })}
        </tbody>
      </table>

      {showPagination && ( // Render pagination control based on the showPagination prop
        <div className="table-pagination">
          <div className="flex items-center">
            <label htmlFor="views-per-page">
              showing {pageIndex + 1} of {pageOptions?.length}
            </label>
            <select
              name="views-per-page"
              id=""
              className="form__select"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              {[10, 20, 50, 100].map((val) => (
                <option value={val}>{val}</option>
              ))}
            </select>
          </div>
          <div className="flex">
            <button
              className="btn pagination-btn"
              disabled={!canPreviousPage}
              onClick={previousPage}
            >
              <TiMediaPlayReverse className="mr-1" /> Prev
            </button>
            <button className="btn curr-btn" disabled>
              {pageIndex}
            </button>
            <button
              className="btn pagination-btn"
              disabled={!canNextPage}
              onClick={nextPage}
            >
              Next <TiMediaPlay className="ml-1" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
